import { generateShortId } from '@o2d/shared';
import { creationDefaults } from '../../core/viewport/zoom-cluster.service.js';
import { textboxPaddingForScale, textboxPaddingHorizontalForScale } from './textbox.renderer.js';
import type { TextBox } from './textbox.interface.js';
import type { TextDocument } from './text-document.type.js';

// Empty paragraph doc — initial value for newly-created textboxes per
// PRD-RTE-TIPTAP-document-model §TR2.
const EMPTY_DOCUMENT: TextDocument = Object.freeze({
  type: 'doc',
  content: Object.freeze([Object.freeze({ type: 'paragraph' })]),
}) as TextDocument;

// Every default below must be a value the options circle can HIGHLIGHT, so a
// fresh textbox shows its own state (Anke, 2026-07-16). `#000` is a text-colour
// swatch; `transparent` is the crossed-out border-colour button; 2 is a
// border-width button; 0 is the "Eckig" corner button. `#1f2937` and width 5
// matched no button and left those rings dark.
const DEFAULT_TEXT_COLOR = '#000';
const MIN_DRAG_WIDTH = 40;
const MIN_DRAG_HEIGHT = 28;
// `transparent` + a real width draws no visible border, yet picking a border
// colour later shows one immediately at a width the circle also highlights.
const DEFAULT_BORDER_COLOR = 'transparent';
const DEFAULT_BORDER_WIDTH = 2;
const DEFAULT_CORNER_RADIUS = 0;
// Smallest strictly-positive content area padding must always leave, in
// world units. Any small positive value works (comfortably visible even at
// the highest zoom bands); 1 was picked as a round number, not tuned.
const MIN_CONTENT_SIZE = 1;

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the readonly TextBox contract.
 * In production builds the freeze is skipped to avoid the small per-call cost.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * Stefan, 2026-09-03 — "choose properties before drawing": optional overrides
 * for the pre-placement-staged options-circle values (see
 * `stagePrePlacementProperty()` in `o2d-svg-optionsbar.element.ts`).
 *
 * Unlike the narrow named-fields options interfaces on the other shapes
 * (`CreateRectangleDefaultsOptions` etc.), this is a generic
 * property-name → value bag: the textbox circle alone resolves well over a
 * dozen intents (fill, both text/border colours, border width/style, text
 * weight/decoration/style/transform/vertical-script, box-shadow, background
 * opacity, corner radius, vertical align, …), and every one of those names
 * already comes from `resolveTextboxProperty()` below — the SAME function
 * that validates the post-creation editing path — so a plain spread is safe
 * without re-naming each field a second time here. `list-type` never appears
 * in this bag: it's the one intent excluded from staging entirely
 * (`NOT_STAGEABLE_INTENTS`) — `text-size`/`text-align` ARE staged (as their
 * resolved `fontSize`/`hAlign` values), see `stagePrePlacementProperty()`.
 */
export type CreateTextBoxDefaultsOptions = Readonly<Record<string, string | number>>;

/**
 * Build a fresh textbox default payload — extracted from the inline
 * `placeTextAt` body in `o2d-whiteboard.element.ts` per PRD-F6 §TR2.
 *
 * - `sizeOpt === null`: bare-click branch, the textbox is centred on the
 *   pointer (top-left = point - size/2). Side length scales inversely with
 *   viewport scale via `creationDefaults`.
 * - `sizeOpt !== null`: drag-to-create branch, the dragged box is the exact
 *   placement rectangle (top-left = pointer); minimum size is enforced so
 *   tiny drags still produce a usable textbox.
 */
export function createTextBoxDefaults(
  viewportScale: number,
  point: { x: number; y: number },
  sizeOpt: { width: number; height: number } | null,
  options?: CreateTextBoxDefaultsOptions,
): TextBox {
  const cd = creationDefaults(viewportScale);
  const w = sizeOpt ? Math.max(MIN_DRAG_WIDTH, sizeOpt.width) : cd.textWidth;
  const h = sizeOpt ? Math.max(MIN_DRAG_HEIGHT, sizeOpt.height) : cd.textHeight;
  const x = sizeOpt ? point.x : point.x - w / 2;
  const y = sizeOpt ? point.y : point.y - h / 2;

  // Bugreport 2026-08-18 (Anke) — padding now grows at low zoom to stay
  // proportional to the (also low-zoom-grown) default font size, see
  // `textboxPaddingForScale`. `MIN_DRAG_WIDTH`/`MIN_DRAG_HEIGHT` don't grow
  // with it, so a minimal drag at low zoom could again collapse the content
  // area to zero/negative — the exact MAJOR bug fixed once already (Review
  // 01, 2026-08-16) for scale 1, reopened for any sufficiently low zoom.
  // Clamped against the ACTUAL box being created (both the click-created and
  // the drag-created path), scaling paddingV/paddingH DOWN TOGETHER so the
  // fixed 2:1 paddingH:paddingV ratio is preserved even when clamping kicks
  // in — not clamped independently, which could otherwise break that ratio.
  const rawPaddingV = textboxPaddingForScale(viewportScale);
  const rawPaddingH = textboxPaddingHorizontalForScale(viewportScale);
  const clampFactor = Math.min(
    1,
    (h - MIN_CONTENT_SIZE) / 2 / rawPaddingV,
    (w - MIN_CONTENT_SIZE) / 2 / rawPaddingH,
  );
  const paddingV = rawPaddingV * clampFactor;
  const paddingH = rawPaddingH * clampFactor;

  const textBox: TextBox = {
    id: generateShortId(),
    type: 'text',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    document: EMPTY_DOCUMENT,
    text: '',
    color: DEFAULT_TEXT_COLOR,
    fontSize: cd.textFontSize,
    fontWeight: 500,
    hAlign: 'left',
    vAlign: 'top',
    background: '',
    borderColor: DEFAULT_BORDER_COLOR,
    borderWidth: DEFAULT_BORDER_WIDTH,
    cornerRadius: DEFAULT_CORNER_RADIUS,
    listType: 'none',
    paddingV,
    paddingH,
    ...options,
  };
  return freezeInDev(textBox);
}
